"use client"

import React, { useEffect, useRef, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Button } from "../../ui/button"
import { getApiBaseUrl } from "../../../config/apiBase"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../../config/featureFlags"

const apiUrl = getApiBaseUrl()
const money = (value, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(value || 0))

const StewardshipModule = ({ actionId }) => {
  const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience)
  const stripeEnabled = isFeatureEnabled(FEATURE_FLAGS.stripeCampaignFunding)
  const manualStewardshipEnabled = isFeatureEnabled(FEATURE_FLAGS.cashCheckStewardship)
  const { getAccessTokenSilently } = useAuth0()
  const [summary, setSummary] = useState(null)
  const stripeRequestKey = useRef({ key: crypto.randomUUID(), fingerprint: null })
  const [tasks, setTasks] = useState([])
  const [form, setForm] = useState({
    actionTaskId: "",
    entryType: "Pledge",
    method: "Cash",
    amount: "",
    description: "",
    contributorName: "",
    isAnonymous: false,
    receiptUrl: "",
    treasurerNote: "",
  })
  const [stripeGift, setStripeGift] = useState({
    actionTaskId: "",
    amount: "",
    contributorName: "",
    isAnonymous: false,
    description: "",
  })
  const [message, setMessage] = useState("")

  const authHeaders = async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
    })
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }
  }

  const loadSummary = async () => {
    const response = await fetch(`${apiUrl}/api/Actions/${actionId}/stewardship`, {
      headers: await authHeaders(),
    })
    if (response.ok) {
      setSummary(await response.json())
    }
  }

  const loadTasks = async () => {
    const response = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks`, {
      headers: await authHeaders(),
    })
    if (response.ok) {
      setTasks(await response.json())
    }
  }

  useEffect(() => {
    loadSummary()
    loadTasks()
  }, [actionId])

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }))

  const addEntry = async (event) => {
    event.preventDefault()
    setMessage("")
    const response = await fetch(`${apiUrl}/api/Actions/${actionId}/stewardship/entries`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({
        ...form,
        actionTaskId: form.actionTaskId ? Number(form.actionTaskId) : null,
        amount: form.amount === "" ? null : Number(form.amount),
      }),
    })
    if (!response.ok) {
      setMessage("That stewardship entry could not be saved.")
      return
    }
    setForm({
      actionTaskId: "",
      entryType: "Pledge",
      method: "Cash",
      amount: "",
      description: "",
      contributorName: "",
      isAnonymous: false,
      receiptUrl: "",
      treasurerNote: "",
    })
    await loadSummary()
    await loadTasks()
  }

  const confirmEntry = async (entry) => {
    const status = entry.entryType === "Expense" ? "Spent" : "Confirmed"
    const response = await fetch(`${apiUrl}/api/Actions/${actionId}/stewardship/entries/${entry.id}/confirm`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ status, receiptUrl: entry.receiptUrl, treasurerNote: entry.treasurerNote }),
    })
    if (!response.ok) {
      setMessage("Only the founder or a treasurer can confirm entries.")
      return
    }
    await loadSummary()
    await loadTasks()
  }

  const startStripeGift = async (event) => {
    event.preventDefault()
    setMessage("")
    const amount = Number(stripeGift.amount)
    if (!amount || amount <= 0) {
      setMessage("Enter a gift amount before opening Stripe.")
      return
    }

    const fingerprint = JSON.stringify({ actionId, ...stripeGift })
    if (stripeRequestKey.current.fingerprint !== fingerprint) {
      stripeRequestKey.current = { key: crypto.randomUUID(), fingerprint }
    }
    const response = await fetch(`${apiUrl}/api/Actions/${actionId}/stripe-funding/checkout`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({
        amount,
        actionTaskId: stripeGift.actionTaskId ? Number(stripeGift.actionTaskId) : null,
        currency: "USD",
        requestKey: stripeRequestKey.current.key,
        description: stripeGift.description || "Campaign stewardship gift",
        contributorName: stripeGift.contributorName,
        isAnonymous: stripeGift.isAnonymous,
      }),
    })
    if (!response.ok) {
      setMessage("Stripe giving is not available for this campaign yet.")
      return
    }

    const checkout = await response.json()
    if (checkout.checkoutUrl) {
      window.location.assign(checkout.checkoutUrl)
      return
    }

    setMessage("Stripe did not return a checkout page.")
  }

  const entries = summary?.entries || []

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-yellow-500">Transparent provision</p>
        <h2 className="text-2xl font-bold">Money, gifts, receipts, and needs</h2>
        <p className="mt-2 text-sm text-gray-400">
          Track {[stripeEnabled && "online gifts", manualStewardshipEnabled && "cash, checks", "supplies, volunteer pledges, expenses, and receipt proof"].filter(Boolean).join(", ")} in one place.
        </p>
      </div>

      {summary && (
        <div className="grid gap-3 md:grid-cols-4">
          <SummaryCard label="Needed" value={money(summary.fundingNeeded)} />
          <SummaryCard label="Received" value={money(summary.received)} />
          <SummaryCard label="Spent" value={money(summary.spent)} />
          <SummaryCard label={summary.shortfall > 0 ? "USD funding gap" : "USD unspent (not surplus)"} value={money(summary.shortfall || summary.surplus)} />
        </div>
      )}

      {stripeEnabled && <form className="grid gap-3 rounded-lg border border-emerald-400/30 bg-emerald-950/20 p-4" onSubmit={startStripeGift}>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Give online</p>
          <h3 className="text-xl font-bold">Give with Stripe</h3>
          {summary?.groupId && <p><a href={`/groups/${summary.groupId}/finance?action=${actionId}`}>Treasurer: issue cash/check receipts or manage the group’s receiving account</a></p>}
          <p>Summary figures are USD only. Stripe receipts exclude original processing fees and refunds. Other currencies are not converted. Pledges and uncleared checks are not funds received. This is not a bank balance.</p>
          <p className="mt-1 text-sm text-gray-300">
            Stripe handles the payment securely. Once Stripe confirms it, TYK adds the gift to this campaign report.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-sm font-semibold">
            Apply to task
            <select
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              value={stripeGift.actionTaskId}
              onChange={(event) => setStripeGift((current) => ({ ...current, actionTaskId: event.target.value }))}
            >
              <option value="">General campaign fund</option>
              {tasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.title}
                  {task.needsFunds ? ` - needs ${money(task.fundingShortfall, task.fundingCurrency || "USD")}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold">
            Gift amount
            <input
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              min="1"
              step="0.01"
              type="number"
              value={stripeGift.amount}
              onChange={(event) => setStripeGift((current) => ({ ...current, amount: event.target.value }))}
              placeholder="50.00"
            />
          </label>
          <label className="text-sm font-semibold md:col-span-2">
            Name, if not anonymous
            <input
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              value={stripeGift.contributorName}
              onChange={(event) => setStripeGift((current) => ({ ...current, contributorName: event.target.value }))}
            />
          </label>
        </div>
        <label className="text-sm font-semibold">
          What is this gift for?
          <textarea
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
            rows="2"
            value={stripeGift.description}
            onChange={(event) => setStripeGift((current) => ({ ...current, description: event.target.value }))}
            placeholder="Example: hot cocoa supplies, carolling flyers, or general Christmas outreach."
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            checked={stripeGift.isAnonymous}
            type="checkbox"
            onChange={(event) => setStripeGift((current) => ({ ...current, isAnonymous: event.target.checked }))}
          />
          Keep my name anonymous in the group report
        </label>
        <Button type="submit">Give with Stripe</Button>
      </form>}

      {manualStewardshipEnabled && <form className="grid gap-3 rounded-lg border border-yellow-500/20 bg-slate-950/60 p-4" onSubmit={addEntry}>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-yellow-500">Treasurer records</p>
          <h3 className="text-xl font-bold">Record cash, checks, supplies, and expenses</h3>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-sm font-semibold">
            Apply to task
            <select
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              value={form.actionTaskId}
              onChange={(event) => updateForm("actionTaskId", event.target.value)}
            >
              <option value="">General campaign fund</option>
              {tasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.title}
                  {task.needsFunds ? ` - needs ${money(task.fundingShortfall, task.fundingCurrency || "USD")}` : ""}
                  {task.needsHelpers ? ` - needs ${task.volunteersStillNeeded} helper${task.volunteersStillNeeded === 1 ? "" : "s"}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold">
            What is this?
            <select
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              value={form.entryType}
              onChange={(event) => updateForm("entryType", event.target.value)}
            >
              <option>Pledge</option>
              <option>Expense</option>
              <option>InKind</option>
              <option>Volunteer</option>
            </select>
          </label>
          <label className="text-sm font-semibold">
            Method
            <select
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              value={form.method}
              onChange={(event) => updateForm("method", event.target.value)}
            >
              <option>Cash</option>
              <option>Check</option>
              {bitcoinEnabled && <option>BTC</option>}
              <option>InKind</option>
            </select>
          </label>
          <label className="text-sm font-semibold">
            Amount
            <input
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              min="0"
              step="0.01"
              type="number"
              value={form.amount}
              onChange={(event) => updateForm("amount", event.target.value)}
            />
          </label>
        </div>
        <label className="text-sm font-semibold">
          What happened?
          <textarea
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
            rows="3"
            value={form.description}
            onChange={(event) => updateForm("description", event.target.value)}
            placeholder="Example: $50 pledged for hot cocoa supplies, or table and coffee urn offered."
          />
        </label>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm font-semibold">
            Name, if not anonymous
            <input
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              value={form.contributorName}
              onChange={(event) => updateForm("contributorName", event.target.value)}
            />
          </label>
          <label className="text-sm font-semibold">
            Receipt or proof link
            <input
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
              value={form.receiptUrl}
              onChange={(event) => updateForm("receiptUrl", event.target.value)}
            />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            checked={form.isAnonymous}
            type="checkbox"
            onChange={(event) => updateForm("isAnonymous", event.target.checked)}
          />
          Keep the giver anonymous in the public report
        </label>
        <Button type="submit">Add to stewardship report</Button>
      </form>}

      {message && <div className="rounded border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm">{message}</div>}

      <div className="space-y-3">
        <h3 className="text-lg font-bold">Stewardship report</h3>
        {entries.length === 0 && <p className="text-sm text-gray-400">No money, supplies, or receipts have been recorded yet.</p>}
        {entries.map((entry) => (
          <div key={entry.id} className="rounded-lg border border-slate-700 bg-slate-950/70 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-bold">
                  {entry.entryType} · {entry.method} {entry.amount ? `· ${money(entry.amount, entry.currency)}` : ""}
                </p>
                <p className="text-sm text-gray-400">
                  {entry.status} · {new Date(entry.createdAt).toLocaleDateString()} · {entry.contributorName || "No name listed"}
                </p>
                {entry.stripePaymentIntentId && (
                  <p className="mt-1 text-xs text-emerald-300">Stripe proof: {entry.stripePaymentIntentId}</p>
                )}
                {entry.description && <p className="mt-2 text-sm">{entry.description}</p>}
                {entry.receiptUrl && (
                  <a className="mt-2 inline-block text-sm text-yellow-400 underline" href={entry.receiptUrl}>
                    View receipt/proof
                  </a>
                )}
              </div>
              {entry.entryType !== "Contribution" && entry.method !== "Stripe" && !["Confirmed", "Spent", "PendingStripe"].includes(entry.status) && (
                <Button type="button" onClick={() => confirmEntry(entry)}>
                  Confirm
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const SummaryCard = ({ label, value }) => (
  <div className="rounded-lg border border-slate-700 bg-slate-950/70 p-4">
    <p className="text-xs uppercase tracking-[0.18em] text-gray-400">{label}</p>
    <p className="mt-2 text-xl font-bold">{value}</p>
  </div>
)

export default StewardshipModule
